import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogScrollContent,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type ClientProgram,
  updateAssignedProgram,
} from "@/integrations/backend/programs";
import ProgramEditorFields, {
  createDefaultProgramEditorValue,
  programToEditorValue,
  type ProgramEditorValue,
} from "./program-editor-fields";

interface EditAssignedProgramDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientProgram: ClientProgram | null;
  clientName: string;
  onSaved: (clientProgram: ClientProgram) => void;
}

export default function EditAssignedProgramDialog({
  open,
  onOpenChange,
  clientProgram,
  clientName,
  onSaved,
}: EditAssignedProgramDialogProps) {
  const [form, setForm] = useState<ProgramEditorValue>(createDefaultProgramEditorValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setForm(createDefaultProgramEditorValue());
      setSaving(false);
      return;
    }

    if (clientProgram) {
      setForm(programToEditorValue(clientProgram.program));
    }
  }, [clientProgram, open]);

  const handleSave = async () => {
    if (!clientProgram) return;

    if (!form.name.trim() || !form.description.trim() || form.duration <= 0 || form.frequency <= 0) {
      toast.error("Please fill in all required program fields.");
      return;
    }

    setSaving(true);
    try {
      const updatedClientProgram = await updateAssignedProgram(clientProgram.id, {
        ...form,
        name: form.name.trim(),
        description: form.description.trim(),
        program_focus: form.program_focus?.trim() || "",
        what_to_expect: form.what_to_expect?.trim() || "",
        variety: form.variety?.trim() || "",
      });
      onSaved(updatedClientProgram);
      toast.success("Assigned program updated successfully");
      onOpenChange(false);
    } catch (error: any) {
      toast.error("Could not update program", { description: error?.message ?? String(error) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogScrollContent className="w-[calc(100vw-2rem)] bg-white text-black sm:max-w-[900px]">
        <DialogHeader className="shrink-0">
          <DialogTitle>Edit Assigned Program</DialogTitle>
          <DialogDescription>
            Update the current program for {clientName || "this client"} without affecting shared templates.
          </DialogDescription>
        </DialogHeader>

        <DialogBody>
          <ProgramEditorFields value={form} onChange={setForm} />
        </DialogBody>

        <DialogFooter className="shrink-0 border-t pt-4 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !clientProgram}>
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogScrollContent>
    </Dialog>
  );
}
